import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Activity, Target, TrendingUp, TrendingDown, AlertTriangle, Info, Gauge } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { LeverageMetrics } from '@/lib/hyperliquid';

interface Props {
  metrics: LeverageMetrics | null;
  className?: string;
  isLoading?: boolean;
}

export function LeverageAnalysisSection({ metrics, className, isLoading }: Props) {
  // Loading state
  if (isLoading) {
    return (
      <div className={cn("space-y-4", className)}>
        <div>
          <h2 className="text-xl font-bold mb-1">Leverage Analysis</h2>
          <p className="text-sm text-muted-foreground">Loading leverage metrics...</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardContent className="pt-6">
                <div className="h-20 bg-muted/30 rounded animate-pulse" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (!metrics || (metrics.avgLeverage == null && metrics.maxLeverage == null)) {
    return (
      <Card className={className}>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Gauge className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-lg">Leverage Analysis</CardTitle>
          </div>
          <CardDescription>
            Insufficient data for leverage analysis. Sync more trading history to see leverage metrics.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const getLeverageColor = (leverage: number | null) => {
    if (leverage == null) return 'text-muted-foreground';
    if (leverage < 3) return 'text-emerald-500';
    if (leverage < 8) return 'text-amber-500';
    return 'text-rose-500';
  };

  const getSharpeColor = (sharpe: number | null) => {
    if (sharpe == null) return 'text-muted-foreground';
    if (sharpe >= 1) return 'text-emerald-500';
    if (sharpe >= 0) return 'text-amber-500';
    return 'text-rose-500';
  };

  const formatPercent = (value: number | null) => {
    if (value == null) return 'N/A';
    const sign = value >= 0 ? '+' : '';
    return `${sign}${(value * 100).toFixed(2)}%`;
  };

  const avgLeverage = metrics.avgLeverage ?? 0;
  const maxLeverage = metrics.maxLeverage ?? 0;
  const leverageAdjustedSharpe = metrics.leverageAdjustedSharpe;
  const skillReturn = metrics.skillReturn;
  const rawReturn = metrics.rawReturn;

  // Calculate leverage contribution as difference between raw and skill returns
  const leverageContribution = rawReturn != null && skillReturn != null
    ? rawReturn - skillReturn
    : null;

  const showHighRiskWarning = avgLeverage > 10 && (leverageAdjustedSharpe == null || leverageAdjustedSharpe < 0.5);

  return (
    <div className={cn("space-y-4", className)}>
      <div>
        <h2 className="text-xl font-bold mb-1">Leverage Analysis</h2>
        <p className="text-sm text-muted-foreground">
          Understand how leverage impacts your risk-adjusted returns
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Avg Effective Leverage Card */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Activity className="h-4 w-4" />
              Avg Effective Leverage
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={cn("text-3xl font-bold font-mono", getLeverageColor(avgLeverage))}>
              {avgLeverage.toFixed(2)}x
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Peak: {maxLeverage.toFixed(2)}x
            </p>
          </CardContent>
        </Card>

        {/* Leverage-Adjusted Sharpe Card */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Target className="h-4 w-4" />
              Leverage-Adjusted Sharpe
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={cn("text-3xl font-bold font-mono", getSharpeColor(leverageAdjustedSharpe))}>
              {leverageAdjustedSharpe != null ? leverageAdjustedSharpe.toFixed(3) : 'N/A'}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Return per unit of leverage
            </p>
          </CardContent>
        </Card>

        {/* Skill Return Card */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Skill Return (1x)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={cn(
              "text-3xl font-bold font-mono",
              skillReturn == null ? 'text-muted-foreground' : skillReturn >= 0 ? 'text-emerald-500' : 'text-rose-500'
            )}>
              {formatPercent(skillReturn)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Return normalized to 1x leverage
            </p>
          </CardContent>
        </Card>

        {/* Leverage Contribution Card */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              {leverageContribution != null && leverageContribution >= 0 ? (
                <TrendingUp className="h-4 w-4" />
              ) : (
                <TrendingDown className="h-4 w-4" />
              )}
              Leverage Contribution
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={cn(
              "text-3xl font-bold font-mono",
              leverageContribution == null ? 'text-muted-foreground' : leverageContribution >= 0 ? 'text-emerald-500' : 'text-rose-500'
            )}>
              {formatPercent(leverageContribution)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Gains from leverage multiplier
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Contextual Alerts based on leverage metrics */}
      {showHighRiskWarning && (
        <Alert variant="destructive" className="border-amber-500/50 bg-amber-500/5">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>High Risk, Low Efficiency</AlertTitle>
          <AlertDescription>
            Your average leverage ({avgLeverage.toFixed(1)}x) is high relative to your risk-adjusted 
            performance (Sharpe: {leverageAdjustedSharpe?.toFixed(3) ?? 'N/A'}). Consider reducing 
            position sizes to improve capital efficiency and lower liquidation risk.
          </AlertDescription>
        </Alert>
      )}

      {/* Alert when leverage is amplifying losses */}
      {skillReturn != null && skillReturn < 0 && leverageContribution != null && leverageContribution < 0 && (
        <Alert variant="destructive">
          <TrendingDown className="h-4 w-4" />
          <AlertTitle>Leverage Amplifying Losses</AlertTitle>
          <AlertDescription>
            Your base strategy is showing a loss ({formatPercent(skillReturn)}), and leverage is 
            amplifying it to {formatPercent(rawReturn)}. Consider reducing leverage until your 
            underlying strategy is profitable at 1x.
          </AlertDescription>
        </Alert>
      )}

      {/* Positive alert for efficient leverage use */}
      {leverageAdjustedSharpe != null && leverageAdjustedSharpe >= 1 && avgLeverage > 1 && (
        <Alert className="border-emerald-500/50 bg-emerald-500/5">
          <TrendingUp className="h-4 w-4 text-emerald-500" />
          <AlertTitle className="text-emerald-600">Efficient Leverage Use</AlertTitle>
          <AlertDescription>
            Your leverage-adjusted Sharpe of {leverageAdjustedSharpe.toFixed(2)} indicates strong 
            risk-adjusted performance. You're using leverage efficiently to amplify a profitable 
            base strategy.
          </AlertDescription>
        </Alert>
      )}

      {/* Info alert about leverage range */}
      {metrics.dailyLeverage && metrics.dailyLeverage.length > 1 && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Info className="h-3 w-3" />
          <span>
            Based on {metrics.dailyLeverage.length} trading days. Leverage ranged from{' '}
            {Math.min(...metrics.dailyLeverage.map(d => d.avgLeverage)).toFixed(1)}x to{' '}
            {Math.max(...metrics.dailyLeverage.map(d => d.maxLeverage)).toFixed(1)}x.
          </span>
        </div>
      )}
    </div>
  );
}
